import { Injectable } from '@angular/core';

import { Action, Actions, createSelector, SelectorOptions, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { ReferralOfferAssignment, ReferralQuestion, ReferralReward } from '@recruitee/http/types';

import { HttpApiService } from '../../../core/http-api.service';
import {
  EntityId,
  EntitySaveMany,
  EntityState,
  EntityStateHelpers,
  EntityUpdateStrategy,
  IEntityState,
} from '../../../core/state';
import { dispatchReferencesByType } from '../../../core/state/entity-state-repos-map';
import { ReferralsRewardRepo } from '../../admin/reward/reward.repository';
import { ReferralsQuestionsRepo } from '../questions/questions.repository';
import {
  LoadReferralsOfferAssigmenDetails,
  LoadReferralsOfferAssignments,
} from './offer-assignment.actions';

type RepositoryType = IEntityState<ReferralOfferAssignment>;

@State<RepositoryType>({
  name: 'offerAssignments',
  defaults: EntityStateHelpers.createDefaultState(),
})
@Injectable()
export class ReferralsPortalOfferAssignmentRepo extends EntityState<ReferralOfferAssignment>() {
  @SelectorOptions({ injectContainerState: false })
  public static withRelations(): (...args: any) => Record<EntityId, ReferralOfferAssignment> {
    return createSelector(
      [
        ReferralsPortalOfferAssignmentRepo.rawEntities,
        ReferralsRewardRepo.withRelations(),
        ReferralsQuestionsRepo.withRelations(),
      ],
      (
        offerAssignments: Record<EntityId, ReferralOfferAssignment>,
        rewards: Record<EntityId, ReferralReward>,
        questions: Record<EntityId, ReferralQuestion>,
      ) =>
        Object.entries(offerAssignments).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: {
              ...value,
              referralReward: rewards[value.referralRewardId],
              referralTemplate: questions[value.referralTemplateId],
            },
          }),
          {},
        ),
    );
  }

  constructor(protected actions$: Actions, private httpApi: HttpApiService) {
    super(actions$, EntityUpdateStrategy.update);
  }

  @Action(LoadReferralsOfferAssignments)
  public loadReferralsOfferAssigment(
    ctx: StateContext<RepositoryType>,
    action: LoadReferralsOfferAssignments,
  ): Observable<any> {
    this.markAsLoading(ctx, action);
    return this.httpApi.get('/offers/active').pipe(
      tap(response => {
        this.saveMany(ctx, action, response.offerAssignments);
        ctx.dispatch(new EntitySaveMany(ReferralsRewardRepo, response.references));
      }),
    );
  }

  @Action(LoadReferralsOfferAssigmenDetails)
  public loadReferralsAssignmentItem(
    ctx: StateContext<RepositoryType>,
    action: LoadReferralsOfferAssigmenDetails,
  ): Observable<any> {
    const offerId = (action.payload && action.payload.id) || null;
    this.markAsLoading(ctx, action);
    return this.httpApi.get(`/offers/${offerId}`).pipe(
      tap(response => {
        dispatchReferencesByType(ctx, response.references);
        this.save(ctx, action, response.offerAssignment);

        if (action.payload && action.payload.active) {
          this.markAsActive(ctx, response.offerAssignment.id);
        }
      }),
    );
  }
}
